/** Dialogue d’impression du navigateur, sans nouvel onglet. */
import "./printBusy.css";

function printBusyShow(): () => void {
  const id = "fx-print-busy";
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("div");
    el.id = id;
    el.className = "fx-print-busy";
    el.setAttribute("role", "status");
    el.setAttribute("aria-live", "polite");
    el.innerHTML =
      '<span class="fx-print-busy__spin" aria-hidden="true"></span><span>Préparation du PDF…</span>';
    document.body.appendChild(el);
  }
  el.hidden = false;
  return () => {
    el.hidden = true;
  };
}

function printNameOf(win: Window, fallback?: string): string {
  const fromDoc = (win.document.title || "").trim();
  const raw = fromDoc && fromDoc !== "." ? fromDoc : (fallback || "").trim();
  return raw || "document";
}

function withSaveName(name: string, run: () => void): void {
  const prev = document.title;
  if (prev === name) {
    run();
    return;
  }
  document.title = name;
  let done = false;
  const restore = () => {
    if (done) return;
    done = true;
    document.title = prev;
    window.removeEventListener("afterprint", restore);
  };
  window.addEventListener("afterprint", restore);
  window.setTimeout(restore, 4000);
  run();
}

function tryPrint(win: Window | null | undefined, fallback?: string): boolean {
  if (!win?.document) return false;
  try {
    const name = printNameOf(win, fallback);
    win.document.title = name;
    withSaveName(name, () => {
      win.focus();
      win.print();
    });
    return true;
  } catch {
    return false;
  }
}

function printFrame(): HTMLIFrameElement {
  const id = "fx-print-frame";
  let iframe = document.getElementById(id) as HTMLIFrameElement | null;
  if (!iframe) {
    iframe = document.createElement("iframe");
    iframe.id = id;
    iframe.setAttribute("aria-hidden", "true");
    iframe.setAttribute("tabindex", "-1");
    iframe.style.cssText = "position:fixed;width:0;height:0;border:0;opacity:0;pointer-events:none;right:0;bottom:0";
    document.body.appendChild(iframe);
  }
  return iframe;
}

export function printHtmlUrl(url: string, frame?: HTMLIFrameElement | null, fileTitle?: string): void {
  if (tryPrint(frame?.contentWindow, fileTitle)) return;
  const iframe = printFrame();
  iframe.onload = () => {
    iframe.onload = null;
    tryPrint(iframe.contentWindow, fileTitle);
  };
  const join = url.includes("?") ? "&" : "?";
  iframe.src = `${url}${join}t=${Date.now()}`;
}

/** Imprime un PDF une fois le fichier reçu — pas avant (génération signée trop lente pour 700 ms). */
export function printPdfUrl(url: string, fileTitle?: string): Promise<void> {
  const hide = printBusyShow();
  return (async () => {
    try {
      const res = await fetch(`${url}${url.includes("?") ? "&" : "?"}t=${Date.now()}`, {
        credentials: "include",
        cache: "no-store",
      });
      const type = (res.headers.get("content-type") || "").toLowerCase();
      if (!res.ok || !type.includes("application/pdf")) {
        window.open(url, "_blank", "noopener");
        return;
      }
      const blob = await res.blob();
      if (blob.size < 80) {
        window.open(url, "_blank", "noopener");
        return;
      }
      const obj = URL.createObjectURL(blob);
      const iframe = printFrame();
      await new Promise<void>((resolve) => {
        const done = () => {
          iframe.onload = null;
          window.clearTimeout(fail);
          const win = iframe.contentWindow;
          if (!win) {
            window.open(obj, "_blank", "noopener");
            resolve();
            return;
          }
          withSaveName(fileTitle || "document", () => {
            try {
              win.focus();
              win.print();
            } catch {
              window.open(obj, "_blank", "noopener");
            }
          });
          window.setTimeout(() => URL.revokeObjectURL(obj), 60_000);
          resolve();
        };
        const fail = window.setTimeout(done, 20_000);
        iframe.onload = done;
        iframe.src = obj;
      });
    } catch {
      window.open(url, "_blank", "noopener");
    } finally {
      hide();
    }
  })();
}
